import type { Agent } from '@/lib/types';
import type { AgentDraft } from '@/lib/types';
import { ACTIVE_STATUSES } from '@/lib/types';
import type { SessionSort } from '@/components/agent/useSessionSort';
import { draftMatchesSearch, searchTerms } from '@/lib/agent-search';
import { getDraftMeta } from '@/components/agent/draft-meta';
import { getTimeBucket, groupByTimeBucket, type TimeBucket } from '@reflex/ui/lib/time-buckets';
import { agentGroupRef } from '@reflex/shared';

/**
 * The timestamp an agent sorts by under a given order. `activity` uses
 * `lastActiveAt` (last stream event, user message, or lifecycle change),
 * falling back to `createdAt` for legacy runs that never recorded it;
 * `created` always uses `createdAt`.
 */
export function agentSortTimestamp(agent: Agent, sort: SessionSort): number {
  return sort === 'activity' ? (agent.lastActiveAt ?? agent.createdAt) : agent.createdAt;
}

export function getNonArchivedAgents(agents: Agent[]): Agent[] {
  return agents.filter((a) => !a.archived);
}

export function getArchivedAgents(agents: Agent[]): Agent[] {
  return agents.filter((a) => a.archived);
}

export function getPinnedAgents(agents: Agent[]): Agent[] {
  return agents.filter((a) => a.pinned);
}

/**
 * Fold `extras` into `primary`, de-duplicating by id and preserving
 * `primary`'s entry (and order) on collision. Used by the sidebar to graft the
 * dedicated pinned-agents query and the server-side search hits onto the
 * paginated list, so a pinned or searched-for run that sits past the loaded
 * pages still appears without double-listing the ones several queries hold.
 * `primary` wins on overlap because it carries the live infinite-scroll cache
 * the rest of the sidebar reads from. Returns `primary` itself when nothing is
 * added, so callers memoizing on the result keep a stable reference.
 */
export function mergeAgentsById(primary: Agent[], ...extras: Agent[][]): Agent[] {
  if (extras.every((extra) => extra.length === 0)) return primary;
  const seen = new Set(primary.map((a) => a.id));
  const additions: Agent[] = [];
  for (const extra of extras) {
    for (const agent of extra) {
      if (seen.has(agent.id)) continue;
      seen.add(agent.id);
      additions.push(agent);
    }
  }
  return additions.length === 0 ? primary : [...primary, ...additions];
}

export function getUnpinnedAgents(agents: Agent[]): Agent[] {
  return agents.filter((a) => !a.pinned);
}

export function getActiveAgents(agents: Agent[]): Agent[] {
  return agents.filter((a) => ACTIVE_STATUSES.has(a.status));
}

export function getActiveAgentCount(agents: Agent[]): number {
  return getActiveAgents(agents).length;
}

export function getUserDisplayName(user: { name?: string | null; login: string }): string {
  return user.name ?? user.login;
}

export type AgentTimeBucket = TimeBucket;

export interface AgentGroup {
  key: AgentTimeBucket;
  label: string;
  agents: Agent[];
}

export function getAgentTimeBucket(timestamp: number, now: Date = new Date()): AgentTimeBucket {
  return getTimeBucket(timestamp, now);
}

/**
 * Group agents into time-based buckets (Today, Yesterday, Last 7 Days,
 * Last 30 Days, Older) based on their `createdAt` timestamp. Input order is
 * preserved within each bucket, and empty buckets are omitted.
 *
 * Thin agent-shaped wrapper over `groupByTimeBucket` from `@reflex/ui` —
 * the same buckets the chat sidebar uses.
 */
export function groupAgentsByTime(agents: Agent[], now: Date = new Date()): AgentGroup[] {
  return groupByTimeBucket(agents, (a) => a.createdAt, now).map(({ key, label, items }) => ({
    key,
    label,
    agents: items,
  }));
}

export type SidebarSessionItem =
  | { kind: 'agent'; id: string; timestamp: number; agent: Agent }
  | { kind: 'draft'; id: string; timestamp: number; draft: AgentDraft };

export interface SidebarSessionGroup {
  key: AgentTimeBucket;
  label: string;
  items: SidebarSessionItem[];
}

/**
 * Merge agents and drafts, sorted by recency within each time bucket. The
 * `sort` order controls how items are ordered within and across buckets
 * (see {@link agentSortTimestamp}); drafts always use their `updatedAt`, their
 * natural last-touched time.
 *
 * Bucket assignment always uses `lastActiveAt ?? createdAt` for agents so that
 * a resumed session moves to "Today" regardless of the active sort mode.
 */
export function groupSidebarSessionsByTime(
  agents: Agent[],
  drafts: AgentDraft[],
  sort: SessionSort,
  now: Date = new Date(),
): SidebarSessionGroup[] {
  const items: SidebarSessionItem[] = [
    ...agents.map(
      (agent): SidebarSessionItem => ({
        kind: 'agent',
        id: agent.id,
        timestamp: agentSortTimestamp(agent, sort),
        agent,
      }),
    ),
    ...drafts.map(
      (draft): SidebarSessionItem => ({
        kind: 'draft',
        id: draft.id,
        timestamp: draft.updatedAt,
        draft,
      }),
    ),
  ].sort((a, b) => b.timestamp - a.timestamp);

  return groupByTimeBucket(
    items,
    (item) =>
      // Bucket by activity time so a resumed session moves to Today even
      // when the active sort is 'created'. Sort order (timestamp) and bucket
      // assignment are intentionally decoupled here.
      item.kind === 'agent' ? (item.agent.lastActiveAt ?? item.agent.createdAt) : item.timestamp,
    now,
  ).map(({ key, label, items: bucketItems }) => ({
    key,
    label,
    items: bucketItems,
  }));
}

/** The key the "not in any group" section uses. Not a real group id. */
export const UNGROUPED_SECTION_KEY = 'ungrouped';

/**
 * What a section stands for: the group it lists. Rows use this to offer the
 * removal that matches the section they are rendered under.
 */
export interface SidebarSectionContext {
  kind: 'group';
  id: string;
  name: string;
}

export interface SidebarGroupSection {
  /** The group's id, or {@link UNGROUPED_SECTION_KEY}. */
  key: string;
  label: string;
  items: SidebarSessionItem[];
  /** What this section stands for, absent for the trailing "none of them" bucket. */
  group: SidebarSectionContext | null;
}

/**
 * Bucket sessions by the group they were filed into, for the sidebar's group
 * view. Groups are ordered by name so the list is stable as runs come and go
 * (unlike time buckets, a group has no natural order), and everything with no
 * group falls into a trailing "Ungrouped" section — including any drafts
 * passed in, which have no membership of their own. (The sidebar passes none:
 * drafts get their own pinned-to-top section instead. `draftsOnly` is the one
 * view that still buckets them here.) A session appears under exactly one
 * section, because it is filed in exactly one group.
 *
 * Within a section, `sort` orders the sessions exactly as it does in the time
 * views, so switching views never reshuffles what you were reading.
 */
export function groupSidebarSessionsByGroup(
  agents: Agent[],
  drafts: AgentDraft[],
  sort: SessionSort,
): SidebarGroupSection[] {
  const sections = new Map<string, SidebarGroupSection>();
  const ungrouped: SidebarSessionItem[] = [];

  for (const agent of agents) {
    const item: SidebarSessionItem = {
      kind: 'agent',
      id: agent.id,
      timestamp: agentSortTimestamp(agent, sort),
      agent,
    };
    const group = agentGroupRef(agent);
    if (!group) {
      ungrouped.push(item);
      continue;
    }
    const section = sections.get(group.id) ?? {
      key: group.id,
      label: group.name,
      items: [],
      group: { kind: 'group', id: group.id, name: group.name },
    };
    section.items.push(item);
    sections.set(group.id, section);
  }

  for (const draft of drafts) {
    ungrouped.push({ kind: 'draft', id: draft.id, timestamp: draft.updatedAt, draft });
  }

  const ordered = [...sections.values()].sort((a, b) => a.label.localeCompare(b.label));
  for (const section of ordered) section.items.sort((a, b) => b.timestamp - a.timestamp);
  if (ungrouped.length > 0) {
    ordered.push({
      key: UNGROUPED_SECTION_KEY,
      label: 'Ungrouped',
      items: ungrouped.sort((a, b) => b.timestamp - a.timestamp),
      group: null,
    });
  }
  return ordered;
}

/**
 * The subset of the sidebar filters a draft can be judged against. A draft is
 * an unsent launch form: it has an owner, a saved timestamp, searchable text,
 * and (sometimes) a repo — and nothing else. Every other filter asks about
 * something only a real run has.
 */
export interface DraftFilters {
  query: string | null;
  timeRange: 'today' | 'week' | 'month' | null;
  /** The owner filter; a draft only survives it when the draft is theirs. */
  userId?: string | null;
  repoSlug?: string | null;
  /**
   * Filters no draft can ever satisfy (status, triage mark, originator kind,
   * association kind, group, label). Any one of them being set means the user
   * asked for something specific about a *run*, so drafts drop out entirely.
   */
  runOnly?: (string | null | undefined)[];
}

/**
 * Narrow the draft list the same way the agent list is narrowed.
 *
 * Owner, search, time range, and repo are answerable from a draft, so they
 * filter it. Anything else the filter bar can ask — a status, a triage mark, a
 * group, a label, a linked PR/issue, a non-agent originator — is a property a
 * draft cannot have, and drafts used to ignore those filters and sit at the top
 * of the results anyway. Asking for "errored runs in this group" and getting
 * your drafts back reads as a broken filter, so a run-specific filter hides
 * them.
 */
export function filterDrafts(drafts: AgentDraft[], filters: DraftFilters): AgentDraft[] {
  if (filters.runOnly?.some((value) => value != null)) return [];

  let result = drafts;
  if (filters.userId) {
    result = result.filter((d) => d.userId === filters.userId);
  }
  if (filters.repoSlug) {
    result = result.filter((d) => getDraftMeta(d).repoSlug === filters.repoSlug);
  }
  const terms = searchTerms(filters.query);
  if (terms.length > 0) {
    result = result.filter((d) => draftMatchesSearch(d, terms));
  }
  if (filters.timeRange) {
    const now = new Date();
    let cutoff: number;
    if (filters.timeRange === 'today') {
      cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    } else if (filters.timeRange === 'week') {
      cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()).getTime();
    } else {
      cutoff = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    }
    result = result.filter((d) => d.updatedAt >= cutoff);
  }
  return result;
}
